import React from 'react';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { FormField } from '../../../global/form/form-field/form-field';
import { CommonPopupBodyFooterLayout } from '../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../global/sub-window/common/common-close-sub-window-buttons';
import { UserGroupsFormVM } from '../user-groups-form.viewmodel';
import { RolesAction } from '../../../../api/roles.dao';
import OptionsButton from '../../../global/options-button/options-button';
import { MultiSelectButton } from '../../../global/multi-select/multi-select-button';
import Text from '../../../global/text/text';
import DefaultButton from '../../../global/default-button/default-button';
import { ADD_TEXT, addText, APPLY_TEXT, CANCEL_TEXT, cancelText, DESCRIPTION, REMOVE_TEXT } from '../../../../constants/common-labels.constants';

interface IProps {
    vm: UserGroupsFormVM;
    intl: IntlShape;
}

const UserGroupsFormPresentation = injectIntl(
    mobxReact.observer((props: IProps) => {
        const {
            intl,
            vm,
            vm: { groupFormDataVM, userGroupId, isSaving, isLoading, handleSubmit, onDeleteGroup }
        } = props;
        const isEdit = !!userGroupId;

        const groupOptions = [
            isEdit && {
                label: intl.formatMessage({
                    id: 'oe.userGroups.deleteUserGroup',
                    description: 'label text',
                    defaultMessage: 'Delete User Group'
                }),
                action: onDeleteGroup,
                danger: true
            }
        ].filter(Boolean);

        const formSubmitHandler = () => {
            const defineFormType = isEdit ? RolesAction.Edit : RolesAction.Create;
            return handleSubmit(intl, defineFormType);
        };

        if (isLoading) {
            return (
                <CommonPopupBodyFooterLayout
                    body={
                        <div className='oe-form meeting-form'>
                            <FormattedMessage
                                description='label text'
                                defaultMessage='Loading group data'
                                id='oe.userGroups.loadingData'
                            />
                        </div>
                    }
                />
            );
        }

        return (
            <CommonPopupBodyFooterLayout
                body={
                    <div>
                        <div className='oe-form'>
                            {
                                <div className='oe-form__body'>
                                    <FormField
                                        label={intl.formatMessage({
                                            id: 'oe.role.name',
                                            description: 'Role name title',
                                            defaultMessage: 'Name'
                                        })}
                                    >
                                        <input
                                            type='text'
                                            className='oe-form-field__control'
                                            value={groupFormDataVM.name}
                                            onChange={({ target: { value } }) => groupFormDataVM.setName(value)}
                                        />
                                        {groupFormDataVM.showValidationErrors() ? (
                                            <div className='oe-form-field__error'>
                                                {groupFormDataVM.getNameError(intl)}
                                            </div>
                                        ) : null}
                                    </FormField>
                                    <FormField
                                        label={intl.formatMessage(DESCRIPTION)}
                                    >
                                        <textarea
                                            className='oe-form-field__control'
                                            value={groupFormDataVM.description}
                                            onChange={({ target: { value } }) => groupFormDataVM.setDescription(value)}
                                        />
                                        {groupFormDataVM.showValidationErrors() ? (
                                            <div className='oe-form-field__error'>
                                                {groupFormDataVM.getDescriptionError(intl)}
                                            </div>
                                        ) : null}
                                    </FormField>
                                    {groupFormDataVM.serverErrorMessage && (
                                        <div className='oe-form-field__error'>{groupFormDataVM.serverErrorMessage}</div>
                                    )}

                                    <div className='event_form__items-section'>
                                        <div className='event_form__items-section__title'>
                                            <FormattedMessage
                                                id='oe.common.users'
                                                description='Label text'
                                                defaultMessage='Users'
                                            />
                                        </div>
                                        <div className='event_form__items-section__selected-items'>
                                            {vm.groupFormDataVM.users
                                                .slice()
                                                .sort((a, b) => a.firstName.localeCompare(b.firstName))
                                                .map(({ username, firstName, lastName }) => (
                                                    <div
                                                        key={username}
                                                        className='event_form__items-section__item-wrapper'
                                                    >
                                                        <Text
                                                            className='event_form__items-section__item-label'
                                                            message={`${firstName} ${lastName}`}
                                                            withEllipsis
                                                        />
                                                        <DefaultButton
                                                            text={<FormattedMessage {...REMOVE_TEXT} />}
                                                            modifier='remove'
                                                            onClick={() => groupFormDataVM.removeUser(username)}
                                                            className='event_form__items-section__remove-item-button'
                                                        />
                                                    </div>
                                                ))}
                                        </div>
                                        <MultiSelectButton
                                            className='event_form__items-section__add-button'
                                            items={vm.usersList}
                                            disabled={groupFormDataVM.users || []}
                                            value={groupFormDataVM.users || []}
                                            keyProperty='username'
                                            nameProperty='name'
                                            text={
                                                <FormattedMessage
                                                    id='oe.coordinators.add'
                                                    description='Button text'
                                                    defaultMessage='{prefix}Add User'
                                                    values={{
                                                        prefix: '+ '
                                                    }}
                                                />
                                            }
                                            applyButtonText={addText()}
                                            resetButtonText={cancelText()}
                                            onChange={groupFormDataVM.setUsers}
                                        />
                                    </div>
                                </div>
                            }
                        </div>
                    </div>
                }
                footer={
                    <>
                        {isSaving && (
                            <span className='oe-popup-layout__footer-status-area'>
                                {intl.formatMessage({
                                    id: 'oe.userGroups.groupIsBeingSaved',
                                    description: 'placeholder about saving',
                                    defaultMessage: 'Group is being saved, please wait...'
                                })}
                            </span>
                        )}

                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(isEdit ? APPLY_TEXT : ADD_TEXT)}
                                modifier={['fat', 'confirm-safe']}
                                onClick={formSubmitHandler}
                                disabled={isSaving}
                            />
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(CANCEL_TEXT)}
                            />
                            {!!groupOptions.length && (
                                <OptionsButton
                                    className='options-button--right-margin-auto'
                                    modifier={['fat', 'options']}
                                    label='More...'
                                    options={groupOptions}
                                    position='top'
                                    align='start'
                                />
                            )}
                        </div>
                    </>
                }
            />
        );
    })
);

export { UserGroupsFormPresentation };
